// src/hooks/useDubbingActions.js - Updated with modal preview and direct download
import { useCallback } from 'react';
import { useToast } from '../contexts/ToastContext';
import apiService from '../services/apiService';

export const useDubbingActions = ({ updateJob, removeJob }) => {
  const { showSuccess, showError, showInfo } = useToast();

  // ✅ UPDATED: Show modal instead of opening in new tab
  const handlePlay = useCallback((job) => {
    if (!job.video_url) {
      showError('Video URL not available');
      return;
    }
    
    // This will be handled by the modal in the parent component
    // We just return the job to trigger modal opening
    return job;
  }, [showError]);

  // ✅ UPDATED: Direct download without opening in new tab
  const handleDownload = useCallback(async (job) => {
    if (!job.video_url) {
      showError('Download URL not available');
      return;
    }

    try {
      showInfo('Starting download...');
      
      // Create a better filename
      const fileName = `${job.project_name || 'dubbed_video'}_${job.target_language || 'dubbed'}.mp4`;
      
      // Fetch the video as blob for direct download
      const response = await fetch(job.video_url);
      
      if (!response.ok) {
        throw new Error('Failed to fetch video file');
      }
      
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess(`Downloaded: ${fileName}`);
      
    } catch (error) {
      console.error('Download error:', error);
      
      // Fallback to the old method if fetch fails
      try {
        const link = document.createElement('a');
        link.href = job.video_url;
        link.download = `${job.project_name || 'dubbed_video'}_${job.target_language || 'dubbed'}.mp4`;
        link.target = '_blank'; // This helps with CORS issues
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        showSuccess('Download started (fallback method)');
      } catch (fallbackError) {
        showError('Failed to download video. Please try again or contact support.');
      }
    }
  }, [showSuccess, showError, showInfo]);

  const handleCancel = useCallback(async (job) => {
    try {
      await apiService.cancelDubbingJob(job.job_id);
      updateJob(job.job_id, { status: 'cancelled' });
      showSuccess('Job cancelled successfully');
    } catch (err) {
      console.error('Cancel job error:', err);
      showError(err.response?.data?.detail || err.message || 'Failed to cancel job');
    }
  }, [updateJob, showSuccess, showError]);

  const handleDelete = useCallback(async (job) => {
    try {
      await apiService.deleteDubbingJob(job.job_id);
      removeJob(job.job_id);
      showSuccess('Job deleted successfully');
    } catch (err) {
      console.error('Delete job error:', err);
      showError(err.response?.data?.detail || err.message || 'Failed to delete job');
    }
  }, [removeJob, showSuccess, showError]);

  const handleRetry = useCallback(async (job) => {
    showInfo('Retry functionality coming soon!');
  }, [showInfo]);

  return {
    handlePlay,
    handleDownload,
    handleCancel,
    handleDelete,
    handleRetry,
  };
};